import { inject } from '@angular/core';
import { CanActivateFn, Router, RedirectCommand, UrlTree } from '@angular/router';
import { PermissionService } from '@/core/permissions/permission.service';
import { PermissionRef } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { ARRECADACAO_ROUTES } from '@/features/financeiro/arrecadacao/arrecadacao.constants';

const R = ARRECADACAO_ROUTES;
const P = PERMISSOES.arrecadacao;
const DASHBOARD_PATH = `/${R.ROOT}/${R.DASHBOARD}`;
const SERVICOS_PATH = `/${R.ROOT}/${R.SERVICOS.ROOT}`;
const SEM_ACESSO_PATH = `/${R.ROOT}/${R.SEM_ACESSO}`;

/**
 * Ordem de preferência de aterrissagem ao entrar em `/arrecadacao/dashboard` (via `''`).
 *
 * D1 libera o card "Arrecadação" do dashboard para QUALQUER role `arrecadacao.*`, mas o antigo
 * redirect estático `'' -> pagamentos` só servia quem tinha `arrecadacao.pagamentos`. Quem só
 * tinha uma das outras 8 funcionalidades do módulo clicava no card e caía direto em Sem acesso —
 * sem sequer chegar a ver o menu do módulo (o `ArrecadacaoContent.ngOnInit`, que chama
 * `menuService.setMenu()`, roda em `/arrecadacao`, mas o dead-end acontecia um nível abaixo,
 * dentro do dashboard).
 *
 * A lista cobre só as funcionalidades do catálogo (`PERMISSOES.arrecadacao`) que têm PÁGINA
 * PRÓPRIA — uma rota que renderiza algo sozinha quando aterrissada diretamente. Isso importa
 * porque "cai em Sem acesso [do módulo] só se nenhuma passar" (ver JSDoc do guard abaixo) só é
 * honesto se a lista cobrir toda funcionalidade ALCANÇÁVEL — faltar uma que TEM página própria
 * equivale a reintroduzir o dead-end original para quem só tem aquela role.
 *
 * `pesquisarBoletos` fica de fora por outro motivo: `PesquisarBoletos` é um diálogo
 * (`<p-dialog [(visible)]="showDialog">`, default `false`), não uma página — é aberto pelo menu
 * do módulo via `command:` (arrecadacao.constants.ts), que seta o flag na instância que o
 * `ArrecadacaoContent` já mantém viva. A rota `/arrecadacao/servicos/pesquisar-boletos` existe
 * (histórico: nada mais linka pra ela) e continua gateada por `permissionGuard`, mas navegar pra
 * ela direto instancia OUTRA `PesquisarBoletos` com `showDialog` no default `false` — tela em
 * branco, não o diálogo. Colocar esse path aqui faria o guard "aterrissar" numa tela em branco
 * dentro do módulo, só um pouco menos grave que o dead-end original. Quem só tem essa role cai no
 * fallback abaixo (com o menu do módulo visível, de onde PODE abrir o diálogo pelo item correto).
 *
 * `relatorios`, `cobranca` e `emailsInadimplentes` são irmãos do dashboard
 * (`/arrecadacao/relatorios`, `/arrecadacao/servicos/...`, não `/arrecadacao/dashboard/...`) —
 * por isso têm path próprio, fora de `DASHBOARD_PATH`. Os alvos de `cobranca`/`emailsInadimplentes`
 * apontam para rotas FOLHA dentro de `/arrecadacao/servicos`, nunca para `/arrecadacao/servicos`
 * cru: `arrecadacao-servicos.routes.ts` redireciona `''` de volta para `/arrecadacao` (dashboard),
 * o que reentraria neste guard e causaria loop infinito.
 *
 * `cartao` agora tem role própria (`arrecadacao.cartao.visualizar`, gate do menu/rota) e por isso
 * entra na ordem, logo após `pagamentos` — é um grid do mesmo dashboard.
 */
export const ORDEM_DE_ATERRISSAGEM: ReadonlyArray<{ escopo: PermissionRef; path: string }> = [
    { escopo: P.pagamentos.escopo, path: `${DASHBOARD_PATH}/${R.PAGAMENTOS}` },
    { escopo: P.cartao.escopo, path: `${DASHBOARD_PATH}/${R.CARTAO}` },
    { escopo: P.boletos.escopo, path: `${DASHBOARD_PATH}/${R.BOLETOS}` },
    { escopo: P.creditos.escopo, path: `${DASHBOARD_PATH}/${R.CREDITOS}` },
    { escopo: P.isencoes.escopo, path: `${DASHBOARD_PATH}/${R.ISENCOES}` },
    { escopo: P.anotacoes.escopo, path: `${DASHBOARD_PATH}/${R.ANOTACOES}` },
    { escopo: P.relatorios.escopo, path: `/${R.ROOT}/${R.RELATORIOS}` },
    { escopo: P.cobranca.escopo, path: `${SERVICOS_PATH}/${R.SERVICOS.GERAR_REMESSA}` },
    { escopo: P.emailsInadimplentes.escopo, path: `${SERVICOS_PATH}/${R.SERVICOS.EMAILS}` }
];

/**
 * Fallback local — NÃO é o `negar()` de `permission.guard.ts` (esse continua intacto, usado pelos
 * guards de rota das funcionalidades). Diferença deliberada: em vez do `/sem-acesso` global
 * (irmão do módulo em `features.routes.ts`), redireciona para `/arrecadacao/sem-acesso` — filha
 * de `/arrecadacao` (`arrecadacao.routes.ts`). Sendo filha, o `ArrecadacaoContent` continua
 * montado e o menu lateral do módulo aparece junto da mensagem, em vez do usuário ficar preso
 * numa página sem nenhum link de saída dentro da Arrecadação.
 *
 * Mesmo contrato do `negar()` compartilhado (D3): `RedirectCommand` + `skipLocationChange`, a URL
 * digitada continua visível na barra de endereços e a rota que falhou nunca chega a instanciar.
 */
function negarNoModulo(router: Router): RedirectCommand {
    return new RedirectCommand(router.parseUrl(SEM_ACESSO_PATH), { skipLocationChange: true });
}

/**
 * Substitui o redirect estático `'' -> pagamentos` por uma escolha dinâmica: a primeira
 * funcionalidade da Arrecadação COM PÁGINA PRÓPRIA que o usuário logado consegue abrir (ver
 * `ORDEM_DE_ATERRISSAGEM` acima — a lista exclui de propósito quem só existe como diálogo, ex.:
 * `pesquisarBoletos`). Se nenhuma passar, cai em `/arrecadacao/sem-acesso` — Sem acesso IN-MODULE,
 * não o global — para o menu do módulo continuar visível (ver `negarNoModulo` acima). Caso
 * legítimo: quem não tem nenhuma role do módulo.
 *
 * NÃO gateia a rota raiz `/arrecadacao` (D7): este guard só escolhe o destino dentro do
 * dashboard, não decide se a entrada é permitida.
 */
export const arrecadacaoDashboardLandingGuard: CanActivateFn = (route): UrlTree | RedirectCommand => {
    const perm = inject(PermissionService);
    const router = inject(Router);

    const alvo = ORDEM_DE_ATERRISSAGEM.find((c) => perm.can(c.escopo));
    return alvo ? router.createUrlTree([alvo.path], { queryParams: route.queryParams }) : negarNoModulo(router);
};
